import React, {
  useMemo,
  useState,
  useRef,
  useEffect,
  useCallback,
} from "react";
import html2canvas from "html2canvas";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "react-query";
import {
  Stepper,
  Product,
  DesignPanel,
  Services,
  Submit,
  SubProducts,
  Tooltip,
  Accessories,
  Editable,
} from "components";
import RightPanel from "layout/right-panel";
import HeadWalls from "components/editor/headwalls";
import {
  deleteAlert,
  successAlert,
  warningAlert,
} from "config/sweet-alert/alert";
import { BedIcon, DiscardIcon, ScaleIcon } from "assets/images";
import {
  checkInfinityDropLocation,
  checkInfinityDropLocation2,
  checkCompassDropLocation2,
  checkAxiomDropLocation,
  checkAxiomDropLocation2,
  checkCompassDropLocation,
  handleCompassDrop,
  handleInfinityAxiomDrop,
  handleInfinityAxiomDrop2,
  handleCompassDrop2,
  handleArrayDrop2,
  handleArrayDrop,
  checkArrayDropLocation,
  checkArrayDropLocation2,
  convertInchesToFeetAndInches,
  headwallData,
  checkUnimountRailDrop,
  handleAccessorieRailDropLocation,
  checkSurfaceMountedRailDrop,
  checkEmbeddedRailDrop,
  handleAccessoryDropLocation,
  checkInfinitySurfaceMountedRailDrop,
  checkConsoleDropLocation,
  checkConsoleDropLocation2,
  handleConsoleDrop,
  handleConsoleDrop2,
  convertToInches,
  checkEloquenceDropLocation,
  checkEloquenceDropLocation2,
  checkSecurityConsoleDropLocation2,
  handleEloquenceDrop,
  handleEloquenceDrop2,
  checkSecurityConsoleDropLocation,
  checkHiddenHeadwallDropLocation,
  checkHiddenHeadwallDropLocation2,
  accessoriesData,
} from "config/calculations/calculation";
import { pixelPerInch, PRODUCT } from "constants/appConstant";
import { useLoaderStore, useHeadWallStore, useServiceStore } from "store";
import { editDesign } from "services/headwallServices";
import Templates from "components/editor/templates";

const models = {
  infinity: "Horizontal Infinity",
  axiom: "Axiom Headwall",
  array: "Array SSH Headwall",
  compass: "Compass Headwall",
  console: "Console Headwall",
  eloquence: "Eloquence Headwall",
  security_console: "Security Console",
  hidden: "Hidden Headwall",
};

function Editor() {
  const [searchParams] = useSearchParams();
  const designId = searchParams.get("id");
  const { setLoading } = useLoaderStore();
  const { serv, setService } = useServiceStore();
  const { values, setValues, resetState } = useHeadWallStore();
  const {
    step,
    add_rail,
    width,
    model,
    headwalls,
    design_image,
    sub_model,
    is_template,
    accessory_rails,
    chased,
    chase_position,
  } = values;

  const zoomControlsRef = useRef(null);
  const [scale, setScale] = useState(1);

  //ref
  const dragItem = useRef();
  const dragPositions = useRef({ x: 0, y: 0 });
  const dragItemNode = useRef();
  const wallRef = useRef(null); //to get Inner wall html;
  const outeWwallRef = useRef(null); //to get outer wall html;
  const railDimensionsRef = useRef(null);

  //states
  //to check the width of wall div
  const [wallWidth, setWallWidth] = useState(0);
  const [dragEntered, setDragEntered] = useState(false);
  //to verify the original name with the updated while editing design
  const [designName, setDesignName] = useState("");
  const [ruler, setRuler] = useState(true);
  const [showBedGraphic, setShowBedGraphic] = useState(false);
  const [showTempWarning, setShowTempWarning] = useState(true);
  const [railMargins, setRailMargins] = useState({});

  useQuery({
    queryKey: ["design", designId],
    queryFn: () => editDesign(designId),
    // The query will not execute until the userId exists
    enabled: !!designId,
    refetchOnWindowFocus: false,
    onSuccess: (data) => {
      const { data: res } = data.data;
      const {
        width,
        ceiling_height,
        headwalls: sections,
        model,
        name,
        accessory_rails,
        sub_model,
      } = res;
      const headwallWidth = convertInchesToFeetAndInches(
        width,
        model,
        sub_model
      );
      const headwallHeight = convertInchesToFeetAndInches(ceiling_height);
      const headwalls = headwallData(sections, model);
      const accessroies = accessoriesData(accessory_rails, model);
      setValues({
        ...res,
        width: headwallWidth,
        step: 3,
        ceiling_height: headwallHeight,
        is_template: false,
        ...headwalls,
        accessory_rails: accessroies,
      });
      setDesignName(name);
    },
  });

  useEffect(() => {
    if (outeWwallRef.current) setWallWidth(outeWwallRef.current.clientWidth);
    //  Scroll code written ////
    // Scroll the actual newly-added/toggled rail into view instead of a
    // fixed pixel guess, since the rail's position shifts depending on
    // ceiling height and how many rails already exist.
    const scrollRailIntoView = (selector) => {
      const el = outeWwallRef.current?.querySelector(selector);
      if (el) {
        el.scrollIntoView({ block: "center", behavior: "smooth" });
        return true;
      }
      return false;
    };
    switch (add_rail) {
      case "top":
        if (!scrollRailIntoView(".rail_top")) {
          outeWwallRef?.current?.scrollTo({
            top: outeWwallRef?.current?.clientHeight / 2 + 450,
            behavior: "smooth",
          });
        }
        break;
      case "bottom":
        if (!scrollRailIntoView(".rail_bottom")) {
          outeWwallRef?.current?.scrollTo({
            top: outeWwallRef?.current?.clientHeight + 1000,
            behavior: "smooth",
          });
        }
        break;
      case "both":
        outeWwallRef?.current?.scrollTo({
          top: outeWwallRef?.current?.clientHeight / 2,
          behavior: "smooth",
        });
        break;

      default:
        // Center the scroll window within the full scrollable height instead
        // of a fixed offset, so the (floor-aligned) headwall lands roughly in
        // view center regardless of product/ceiling height, rather than near
        // the top.
        outeWwallRef?.current?.scrollTo({
          top:
            (outeWwallRef?.current?.scrollHeight -
              outeWwallRef?.current?.clientHeight) /
            2,
          behavior: "smooth",
        });
        break;
    }
  }, [width, model, add_rail, outeWwallRef.current?.clientWidth, scale]);

  useEffect(() => {
    // Chase (Infinity only) sits far to the left/center/right of a much
    // wider canvas once enabled, so bring it into view the same way rails
    // are scrolled into view above.
    if (chased) {
      const chaseEl = outeWwallRef.current?.querySelector("#infinity_chase");
      chaseEl?.scrollIntoView({
        block: "nearest",
        inline: "center",
        behavior: "smooth",
      });
    }
  }, [chased, chase_position]);

  const handleDragStart = useCallback(
    (
      item,
      e = null,
      railIndex = null, //getting image in railIndex for accessories
      serviceIndex = null,
      rowIndex = null //for compass rows
    ) => {
      e.dataTransfer.effectAllowed = "copyMove";
      dragItemNode.current = e.target;
      const draggedElement = e.target;

      // Set the clone as the drag image
      if (item.is_accessory && !item?.u_id) {
        e.dataTransfer.setDragImage(railIndex, railIndex.width / 2, 0);
      } else {
        if (
          (item?.is_slide &&
            (model === PRODUCT.INFINITY || model === PRODUCT.CONSOLE)) ||
          item.is_accessory
        )
          e.dataTransfer.setDragImage(
            draggedElement,
            e.target.offsetWidth / 2,
            0
          );
        else
          e.dataTransfer.setDragImage(
            draggedElement,
            e.target.offsetWidth / 2,
            e.target.offsetHeight / 2
          );
      }

      dragItem.current = {
        service: item,
        railIndex: railIndex,
        serviceIndex: serviceIndex,
        dragServiceHeight: e.target.offsetHeight,
        dragServiceWidth: e.target.offsetWidth,
        rowIndex: rowIndex, //for compass
      };
    },
    [model]
  );

  const clickArrowRight = (e, serv, railInd, rowInd, type) => {
    const prevX = serv.positions.x;

    if (type !== "compass") {
      serv.positions.x = serv.positions.x + 10;
      setService(serv, true);
    }

    dragItem.current = {
      service: serv,
      railIndex: railInd,
      serviceIndex: 0,
      dragServiceHeight: serv.height,
      dragServiceWidth: type === "infinity" ? serv.gang_width : serv.width,
      rowIndex: rowInd, //for compass
    };
    // dragPositions.current = { x: serv.positions.x  + 10, y: 0 };

    // handleDragStart2(serv,railInd,0,rowInd)
    handleDragEnter2(e, railInd, rowInd);
    handleonDragOver2(serv, "rail", dragItem.current);

    handleDrop2(
      serv,
      "rail",
      railInd,
      rowInd,
      dragItem.current,
      "ArrowRight",
      prevX
    );
  };

  const clickArrowLeft = (e, serv, railInd, rowInd, type) => {
    const prevX = serv.positions.x;

    if (type !== "compass") {
      serv.positions.x = serv.positions.x - 10;
      setService(serv, true);
    }
    dragItem.current = {
      service: serv,
      railIndex: railInd,
      serviceIndex: 0,
      dragServiceHeight: serv.height,
      dragServiceWidth: type === "infinity" ? serv.gang_width : serv.width,
      rowIndex: rowInd, //for compass
    };
    // dragPositions.current = { x: serv.positions.x -10, y: 0 };

    handleDragEnter2(e, railInd, rowInd);
    handleonDragOver2(serv, "rail", dragItem.current);

    handleDrop2(
      serv,
      "rail",
      railInd,
      rowInd,
      dragItem.current,
      "ArrowLeft",
      prevX
    );
  };

  const clickArrowUp = (e, serv, railInd, rowInd, type) => {
    const prevY = serv.positions.y;
    let serviceMargin = serv?.is_slide ? 0 : 1.5;

    if (type === "array") {
      serviceMargin = 0.75;
    }

    let serviceHeight = serv.height + serviceMargin;

    serv.positions.y = serv.positions.y - serviceHeight / pixelPerInch;
    setService(serv, true);
    dragItem.current = {
      service: serv,
      railIndex: railInd,
      serviceIndex: 0,
      dragServiceHeight: serviceHeight,
      dragServiceWidth: serv.width,
      rowIndex: rowInd, //for compass
    };
    // dragPositions.current = { x: serv.positions.x -10, y: 0 };
    handleDragEnter2(e, railInd, rowInd);

    handleonDragOver2(serv, "rail", dragItem.current);

    handleDrop2(
      serv,
      "rail",
      railInd,
      rowInd,
      dragItem.current,
      "ArrowUp",
      prevY
    );
  };

  const clickArrowDown = (e, serv, railInd, rowInd, type) => {
    const prevY = serv.positions.y;
    let serviceMargin = serv?.is_slide ? 0 : 1.5;

    if (type === "array") {
      serviceMargin = 0.75;
    }

    let serviceHeight = serv.height + serviceMargin;

    serv.positions.y = serv.positions.y + serviceHeight / pixelPerInch;
    setService(serv, true);
    dragItem.current = {
      service: serv,
      railIndex: railInd,
      serviceIndex: 0,
      dragServiceHeight: serviceHeight,
      dragServiceWidth: serv.width,
      rowIndex: rowInd, //for compass
    };
    // dragPositions.current = { x: serv.positions.x -10, y: 0 };
    handleDragEnter2(e, railInd, rowInd);

    handleonDragOver2(serv, "rail", dragItem.current);

    handleDrop2(
      serv,
      "rail",
      railInd,
      rowInd,
      dragItem.current,
      "ArrowDown",
      prevY
    );
  };

  const handleDragEnter2 = (e, railIndex = null, row = null) => {
    //getting here rail index to check in which section of compass elements gets dragged for compass slide calculations
    // e.stopPropagation();
    const { service } = dragItem.current;
    setDragEntered(true);
    //get the dimesions of droppable div
    const dropContainerRect = e?.target?.getBoundingClientRect();
    // Get the parent div (outer div)
    let parentRect;
    let parentWidth;
    let parentHeight;
    let width;
    let left;
    let top;
    if (
      (model === PRODUCT.COMPASS || model === PRODUCT.ARRAY) &&
      service?.is_slide
    ) {
      const parentBlock = document.getElementById(
        `compass_section_${railIndex}`
      );
      parentRect = parentBlock.getBoundingClientRect();
      parentWidth = parentRect.width;
      width = parentRect.width;
      left = parentRect.left;
      parentHeight = parentRect.height;
      top = parentRect.top;
    } else if (
      model === PRODUCT.COMPASS &&
      !service?.is_slide &&
      !service?.is_accessory_rail &&
      !service?.is_accessory
    ) {
      const parentBlock = document.getElementById(
        `compass_section_${railIndex}`
      );
      parentRect = parentBlock?.getBoundingClientRect();
      parentWidth = e.target.offsetWidth;
      parentHeight = parentRect?.height;
      width = dropContainerRect.width;
      left = dropContainerRect.left;
      top = parentRect?.top;
    }
    //calculation for accessory rails
    else if (
      model !== PRODUCT.INFINITY &&
      (service?.is_accessory_rail || service?.is_accessory)
    ) {
      const parentBlock = document.getElementsByClassName("compass-board");
      parentRect = parentBlock[0]?.getBoundingClientRect();
      parentWidth = parentRect.width;
      width = parentRect.width;
      left = parentRect.left;
      parentHeight = parentRect.height - 50; //subtracting 50px to get exact height  of compass board as subtracting value of sections div;
      top = parentRect.top;
    } else if (
      model === PRODUCT.INFINITY &&
      (service?.is_accessory_rail || service?.is_accessory)
    ) {
      const parentBlock = document.getElementById("infinity-board");
      parentRect = parentBlock.getBoundingClientRect();
      parentWidth = parentRect.width;
      width = parentRect.width;
      left = parentRect.left;
      parentHeight = parentRect.height;
      top = parentRect.top;
    } else if (model === PRODUCT.ARRAY && !service?.is_slide) {
      const rowBlock = document.getElementById(`array_row_${railIndex}_${row}`);
      const rowRect = rowBlock?.getBoundingClientRect();
      parentWidth = e.target.offsetWidth;
      parentHeight = rowRect?.height;
      width = rowRect?.width;
      left = rowRect?.left;
      top = rowRect?.top;
    } else {
      parentWidth = e?.target?.offsetWidth;
      parentHeight = e?.target?.offsetHeight;
      width = dropContainerRect.width;
      left = dropContainerRect.left;
      top = dropContainerRect.top;
    }
    if (model === PRODUCT.INFINITY) {
      if (values.chase_position === "center") {
        const parentBlock = document.getElementById("infinity-board");
        parentRect = parentBlock.getBoundingClientRect();
        parentWidth = parentRect.width;
        width = parentRect.width;
      } else {
        parentWidth = Math.round(convertToInches(values.width) / pixelPerInch);
        width = Math.round(convertToInches(values.width) / pixelPerInch);
      }
      // }
      railDimensionsRef.current = {
        parentWidth,
        parentHeight,
        width,
        left,
        top,
      };
    } else {
      railDimensionsRef.current = {
        parentWidth,
        parentHeight,
        width,
        left,
        top,
      };
    }
  };

  const handleDragEnter = (e, railIndex = null, row = null) => {
    if (dragItem.current) {
      //getting here rail index to check in which section of compass elements gets dragged for compass slide calculations
      e.stopPropagation();
      const { service } = dragItem.current;
      setDragEntered(true);
      //get the dimesions of droppable div
      const dropContainerRect = e.target.getBoundingClientRect();
      // Get the parent div (outer div)
      let parentRect;
      let parentWidth;
      let parentHeight;
      let width;
      let left;
      let top;
      if (
        (model === PRODUCT.COMPASS || model === PRODUCT.ARRAY) &&
        service?.is_slide
      ) {
        const parentBlock = document.getElementById(
          `compass_section_${railIndex}`
        );
        parentRect = parentBlock.getBoundingClientRect();
        parentWidth = parentRect.width;
        width = parentRect.width;
        left = parentRect.left;
        parentHeight = parentRect.height;
        top = parentRect.top;
      } else if (
        model === PRODUCT.COMPASS &&
        !service?.is_slide &&
        !service?.is_accessory_rail &&
        !service?.is_accessory
      ) {
        const parentBlock = document.getElementById(
          `compass_section_${railIndex}`
        );
        parentRect = parentBlock?.getBoundingClientRect();
        parentWidth = e.target.offsetWidth;
        parentHeight = parentRect?.height;
        width = dropContainerRect.width;
        left = dropContainerRect.left;
        top = parentRect?.top;
      }
      //calculation for accessory rails
      else if (
        model !== PRODUCT.INFINITY &&
        (service?.is_accessory_rail || service?.is_accessory)
      ) {
        const parentBlock = document.getElementsByClassName("compass-board");
        parentRect = parentBlock[0]?.getBoundingClientRect();
        parentWidth = parentRect?.width;
        width = parentRect?.width;
        left = parentRect?.left;
        parentHeight = parentRect?.height - 50; //subtracting 50px to get exact height  of compass board as subtracting value of sections div;
        top = parentRect?.top;
      } else if (
        model === PRODUCT.INFINITY &&
        (service?.is_accessory_rail || service?.is_accessory)
      ) {
        const parentBlock = document.getElementById("infinity-board");
        parentRect = parentBlock.getBoundingClientRect();
        parentWidth = parentRect.width;
        width = parentRect.width;
        left = parentRect.left;
        parentHeight = parentRect.height;
        top = parentRect.top;
      } else if (
        model === PRODUCT.ELOQUENCE &&
        // sub_model === "rail" &&
        service.is_slide &&
        (service.name === "Track Mounted" || service.name === "Rail Mounted")
      ) {
        const parentBlock = document.getElementById("r_eloquence");
        parentRect = parentBlock.getBoundingClientRect();
        parentWidth = parentRect.width;
        width = parentRect.width;
        left = parentRect.left;
        parentHeight = parentRect.height;
        top = parentRect.top;
      } else if (model === PRODUCT.ELOQUENCE) {
        const parentBlock = document.getElementById("equolence_rail");
        parentRect = parentBlock.getBoundingClientRect();
        parentWidth = parentRect.width;
        width = parentRect.width;
        left = parentRect.left;
        parentHeight = parentRect.height;
        top = parentRect.top;
      } else if (model === PRODUCT.ARRAY && !service?.is_slide) {
        const rowBlock = document.getElementById(
          `array_row_${railIndex}_${row}`
        );
        const rowRect = rowBlock?.getBoundingClientRect();
        parentWidth = e.target.offsetWidth;
        parentHeight = rowRect?.height;
        width = rowRect?.width;
        left = rowRect?.left;
        top = rowRect?.top;
      } else {
        parentWidth = e.target.offsetWidth;
        parentHeight = e.target.offsetHeight;
        width = dropContainerRect.width;
        left = dropContainerRect.left;
        top = dropContainerRect.top;
      }
      railDimensionsRef.current = {
        parentWidth,
        parentHeight,
        width,
        left,
        top,
      };
      setService(serv, false);
    }
  };

  const handleonDragOver2 = (e, draggingArea, item) => {
    // e.preventDefault();
    // e.stopPropagation();
    if (draggingArea === "rail") {
      // // Get the dimensions of the child div
      const { dragServiceHeight, dragServiceWidth, service } = item;

      //to get the rail dimensions
      const deltaX = e.positions.x;
      // e.positions.x - railDimensionsRef.current?.left - dragServiceWidth / 2;

      const deltaY = e.positions.y;
      // e.positions.y - railDimensionsRef.current?.top - dragServiceHeight / 2;
      let newPosition = {
        x: deltaX,
        y: deltaY,
      };

      // // Calculate the boundaries within which the child div can move
      let maxX =
        railDimensionsRef.current?.parentWidth -
        dragServiceWidth / pixelPerInch;
      if (model === PRODUCT.AXIOM)
        if (service.category === "Medical Gas Outlet") {
          maxX =
            railDimensionsRef.current?.parentWidth -
            (dragServiceWidth + 2.375) / pixelPerInch;
        } else {
          maxX =
            railDimensionsRef.current?.parentWidth -
            (dragServiceWidth + 0.125) / pixelPerInch;
        }

      const maxY =
        railDimensionsRef.current?.parentHeight -
        dragServiceHeight / pixelPerInch;

      //------start of x while adding chase on left or right side-----//
      const startX = railMargins.start;
      const lastX = railMargins.end;
      const startY = railMargins.top;
      const lastY = railMargins.bottom;
      // // Restrict the child div's movement within the parent div
      const restrictedX = Math.max(
        startX,
        Math.min(newPosition.x, maxX - lastX)
      );
      const restrictedY = Math.max(
        startY,
        Math.min(newPosition.y, maxY - lastY)
      );

      // // Update the position of the child div
      if (model === PRODUCT.AXIOM && service?.is_slide) {
        if (restrictedX < railDimensionsRef.current?.width / 2) {
          dragPositions.current = { x: 0, y: restrictedY };
        } else {
          dragPositions.current = {
            x: railDimensionsRef.current?.width,
            y: restrictedY,
          };
        }
      } else {
        dragPositions.current = { x: restrictedX, y: restrictedY };
      }
    }
  };

  const handleonDragOver = (e, draggingArea) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggingArea === "rail" && dragItem.current) {
      // // Get the dimensions of the child div
      let { dragServiceHeight, dragServiceWidth, service } = dragItem.current;

      const serviceHeight = service.is_accessory ? 0 : dragServiceHeight / 2;
      const serviceWidth =
        service?.mount_type === "track" && service.is_accessory
          ? 0
          : dragServiceWidth / 2;
      //to get the rail dimensions

      const deltaX =
        model === "eloquence" && service.is_slide
          ? e.clientX - railDimensionsRef.current?.left - serviceWidth / 2
          : e.clientX - railDimensionsRef.current?.left - serviceWidth;

      const deltaY = e.clientY - railDimensionsRef.current?.top - serviceHeight;
      let newPosition = {
        x: deltaX,
        y: deltaY,
      };
      // // Calculate the boundaries within which the child div can move
      const maxX =
        service?.mount_type === "track" ||
        (model === "eloquence" && service.is_slide)
          ? railDimensionsRef.current?.parentWidth
          : railDimensionsRef.current?.parentWidth - dragServiceWidth;
      const maxY =
        model === "eloquence" && service.is_slide
          ? railDimensionsRef.current?.parentHeight
          : railDimensionsRef.current?.parentHeight - dragServiceHeight;
      //------start of x while adding chase on left or right side-----//
      const startX = railMargins.start;
      const lastX = railMargins.end;
      const startY = railMargins.top;
      const lastY = railMargins.bottom;
      // // Restrict the child div's movement within the parent div
      const restrictedX = Math.max(
        startX,
        Math.min(newPosition.x, maxX - lastX)
      );
      const restrictedY = Math.max(
        startY,
        Math.min(newPosition.y, maxY - lastY)
      );
      // console.log({ restrictedX, restrictedY });
      // // Update the position of the child div
      if (model === PRODUCT.AXIOM && service?.is_slide) {
        if (restrictedX < railDimensionsRef.current?.width / 2)
          dragPositions.current = { x: 0, y: restrictedY };
        else
          dragPositions.current = {
            x: railDimensionsRef.current?.width,
            y: restrictedY,
          };
      } else dragPositions.current = { x: restrictedX, y: restrictedY };
    }
  };

  const generateImage = async () => {
    try {
      setLoading(true);
      const content = wallRef.current;
      // Hiding scale
      const horizontalScale = content.querySelector(".scale");
      const verticalScale = content.querySelector(".vertical-scale");
      const hospitalBed = content.querySelector(".bed-graphic");
      if (horizontalScale && verticalScale) {
        horizontalScale.style.visibility = "hidden"; // Hide each element
        verticalScale.style.visibility = "hidden"; // Hide each element
      }
      // Hiding bed
      if (hospitalBed) hospitalBed.style.visibility = "hidden";

      const scrollableHeight = content.scrollHeight;
      const scrollableWidth = content.scrollWidth;

      const result = await html2canvas(content, {
        proxy: "https://dev-api.hsiheadwalls.com/html2canvas",
        height: scrollableHeight,
        width: scrollableWidth,
        windowWidth: scrollableWidth,
        windowHeight: scrollableHeight,
        scrollX: 0,
        scrollY: 0,
        allowTaint: false,
        imageTimeout: 240000,
        // useCORS: true,
        // logging: true,
        // Transparent background so empty canvas area is unambiguously
        // alpha === 0 and can be reliably distinguished from the product
        // when cropping below (a partially-transparent fill risks getting
        // flattened to opaque, which would make the whole capture look
        // "non-transparent" and defeat the crop).
        backgroundColor: null,
      });

      // The product only occupies part of the captured canvas (the rest is
      // empty wall/canvas space needed for the editor's drag-and-drop layout,
      // plus thin decorative lines like the wrapper's top/bottom border that
      // sit far from the actual product). A plain min/max bounding box over
      // every non-transparent pixel gets dragged out by those thin lines, so
      // instead find the row-band with the most opaque pixels (the product
      // itself) and crop to that.
      const canvas = result;
      const ctx = canvas.getContext("2d");
      const { width: canvasWidth, height: canvasHeight } = canvas;
      const imageData = ctx.getImageData(0, 0, canvasWidth, canvasHeight);
      const data = imageData.data;

      const rowCounts = new Uint32Array(canvasHeight);
      for (let y = 0; y < canvasHeight; y++) {
        const rowStart = y * canvasWidth * 4;
        let count = 0;
        for (let x = 0; x < canvasWidth; x++) {
          if (data[rowStart + x * 4 + 3] > 128) count++;
        }
        rowCounts[y] = count;
      }

      let bestStart = -1,
        bestEnd = -1,
        bestMass = 0;
      let runStart = -1,
        runMass = 0;
      for (let y = 0; y <= canvasHeight; y++) {
        const nonEmpty = y < canvasHeight && rowCounts[y] > 0;
        if (nonEmpty) {
          if (runStart === -1) {
            runStart = y;
            runMass = 0;
          }
          runMass += rowCounts[y];
        } else if (runStart !== -1) {
          if (runMass > bestMass) {
            bestMass = runMass;
            bestStart = runStart;
            bestEnd = y - 1;
          }
          runStart = -1;
        }
      }

      if (bestStart !== -1) {
        let minX = canvasWidth,
          maxX = -1;
        for (let y = bestStart; y <= bestEnd; y++) {
          const rowStart = y * canvasWidth * 4;
          for (let x = 0; x < canvasWidth; x++) {
            if (data[rowStart + x * 4 + 3] > 128) {
              if (x < minX) minX = x;
              if (x > maxX) maxX = x;
            }
          }
        }

        const productWidth = maxX - minX + 1;
        const productHeight = bestEnd - bestStart + 1;
        const padding = 20; // equal on every side, so the product is centered

        const outCanvas = document.createElement("canvas");
        outCanvas.width = productWidth + padding * 2;
        outCanvas.height = productHeight + padding * 2;
        const outCtx = outCanvas.getContext("2d");
        outCtx.drawImage(
          canvas,
          minX,
          bestStart,
          productWidth,
          productHeight,
          padding,
          padding,
          productWidth,
          productHeight
        );
        setValues({ ...values, design_image: outCanvas.toDataURL() });
      } else {
        setValues({ ...values, design_image: result.toDataURL() });
      }
    } catch (err) {
      console.log("err", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => {
    if (step > 1)
      deleteAlert(
        handleDiscard,
        "Are you sure you wish to discard this screen?",
        "Please keep in mind that entire screen will be deleted and this process cannot be reversible.",
        "Yes, Discard!"
      );
  };

  const handleDiscard = () => {
    resetState();
    setService("");
    successAlert("Ok", "Your file has been deleted.");
  };

  const component = useMemo(() => {
    switch (step) {
      case 1:
        return <Product />;
      case 2:
        return <SubProducts />;
      case 3:
        return <DesignPanel />;
      case 4:
        return <Services handleDragStart={handleDragStart} />;
      case 5:
        return <Accessories handleDragStart={handleDragStart} />;
      case 6:
        return <Submit />;
      default:
        return <Product />;
    }
  }, [step, handleDragStart]); //rightPanelSteps,

  useEffect(() => {
    let margins = { start: 0, end: 0, top: 0, bottom: 0, between: 0 };
    const serv = dragItem.current?.service;
    if (
      model === PRODUCT.INFINITY &&
      !serv?.is_accessory &&
      !serv?.is_accessory_rail
    ) {
      if (!chased || chase_position === "center")
        margins = {
          start: 12.25,
          end: 12.25,
          top: 0,
          bottom: 0,
          between: 12.25,
        };
      else if (chase_position === "left")
        margins = {
          start: 107.25,
          end: 12.25,
          top: 0,
          bottom: 0,
          between: 12.25,
        };
      else if (chase_position === "right")
        margins = {
          start: 12.25,
          end: 107.25,
          top: 0,
          bottom: 0,
          between: 12.25,
        };
    } else if (model === PRODUCT.AXIOM && !serv?.is_accessory) {
      margins = {
        start: 20.0892857143, //to manage 1.125 from start
        end: 18.732142857, //to manage 1.125 from end
        top: 228.2, //to manage 0.75 from end
        bottom: 0,
        between: 2.23214285714,
      };
    } else if (model === PRODUCT.COMPASS) {
      margins = { start: 0, end: 0, top: 0, bottom: 0, between: 0 };
    } else if (
      model === PRODUCT.ELOQUENCE &&
      serv?.name !== "Track Mounted" &&
      serv?.name !== "Rail Mounted"
    ) {
      margins = {
        start: 11.1607142857,
        end: 11.1607142857,
        top: 0,
        bottom: 0,
        between: 0,
      };
    } else if (model === PRODUCT.HIDDEN) {
      //0.1250 margins from side
      margins = {
        start: 24.5535714286,
        end: 24.5535714286,
        top: 0,
        bottom: 0,
        between: 0,
      };
    } else if (model === PRODUCT.ARRAY && !serv?.is_accessory) {
      margins = {
        start: 4.46428571429,
        end: 16.46428571429, //4.46428571429
        top: 6.69642857143,
        bottom: 6.69642857143,
        between: 8.92857142857,
      };
    }
    setRailMargins(margins);
  }, [chased, model, chase_position, dragEntered]);

  const handleDrop = useCallback(
    (e, dropArea, railIndex = null, rowIndex = null) => {
      e.stopPropagation();
      if (dragItem.current) {
        setDragEntered(false);
        const rails = [...headwalls];
        const {
          railIndex: dragRailIndex, //from where service is dragged
          service,
          rowIndex: dragRowIndex, //from where service is dragged
        } = dragItem.current;
        let dropLocation = true;
        let accessorieRails;
        let newServiceObj = { ...service };

        //check droplocation only for the services that are dropped on rail and for services
        if (!service?.is_accessory_rail && !service.is_accessory) {
          if (dropArea === "rail") {
            if (model === PRODUCT.INFINITY)
              dropLocation = checkInfinityDropLocation(
                railIndex,
                dragItem,
                values,
                dragPositions,
                railDimensionsRef,
                railMargins
              );
            else if (model === PRODUCT.AXIOM) {
              if (service.category === "Gas") railMargins["between"] = 20; //margins for gas service is different
              dropLocation = checkAxiomDropLocation(
                railIndex,
                dragItem,
                values,
                dragPositions,
                railDimensionsRef,
                railMargins,
                accessory_rails
              );
            } else if (model === PRODUCT.COMPASS) {
              dropLocation = checkCompassDropLocation(
                rowIndex,
                railIndex,
                dragItem,
                values,
                dragPositions,
                railMargins,
                accessory_rails
              );
            } else if (model === PRODUCT.ARRAY) {
              dropLocation = checkArrayDropLocation(
                rowIndex,
                railIndex,
                dragItem,
                values,
                dragPositions,
                railMargins,
                railDimensionsRef,
                accessory_rails
              );
            } else if (model === PRODUCT.CONSOLE) {
              dropLocation = checkConsoleDropLocation(
                railIndex,
                dragItem,
                values,
                dragPositions,
                railMargins,
                railDimensionsRef
              );
            } else if (model === PRODUCT.ELOQUENCE) {
              dropLocation = checkEloquenceDropLocation(
                railIndex,
                dragItem,
                values,
                dragPositions,
                railMargins,
                railDimensionsRef
              );
            } else if (model === PRODUCT.SECURITY) {
              dropLocation = checkSecurityConsoleDropLocation(
                railIndex,
                dragItem,
                values,
                dragPositions
              );
            } else if (model === PRODUCT.HIDDEN) {
              dropLocation = checkHiddenHeadwallDropLocation(
                railIndex,
                dragItem,
                values,
                dragPositions,
                railMargins,
                railDimensionsRef
              );
            }
          } else if (
            dropArea !== "rail" &&
            is_template &&
            showTempWarning &&
            service?.u_id
          ) {
            warningAlert(
              "Please don't remove the services as it may no longer meet the FGI minimum guidelines."
            );
            setShowTempWarning(false);
            return;
          }
          //drop from service panel and interchange of services between panels
          if (model === PRODUCT.INFINITY || model === PRODUCT.AXIOM) {
            handleInfinityAxiomDrop(
              e,
              rails,
              railIndex,
              dragRailIndex,
              values,
              dragPositions,
              railDimensionsRef,
              railMargins,
              dropLocation,
              service,
              dragItemNode
            );
          }
          //calculation for compass
          else if (model === PRODUCT.COMPASS) {
            handleCompassDrop(
              e,
              rails,
              railIndex,
              dragRailIndex,
              values,
              dragPositions,
              railDimensionsRef,
              railMargins,
              dropLocation,
              service,
              dragItemNode,
              rowIndex,
              dragRowIndex
            );
          }
          //calculation for array
          else if (model === PRODUCT.ARRAY) {
            handleArrayDrop(
              e,
              rails,
              railIndex,
              dragRailIndex,
              values,
              dragPositions,
              railDimensionsRef,
              railMargins,
              dropLocation,
              service,
              dragItemNode,
              rowIndex,
              dragRowIndex
            );
          } else if (model === PRODUCT.CONSOLE || model === PRODUCT.SECURITY) {
            handleConsoleDrop(
              e,
              rails,
              railIndex,
              dragRailIndex,
              values,
              dragPositions,
              railDimensionsRef,
              railMargins,
              dropLocation,
              service,
              dragItemNode,
              rowIndex,
              dragRowIndex
            );
          } else if (model === PRODUCT.ELOQUENCE || model === PRODUCT.HIDDEN) {
            handleEloquenceDrop(
              e,
              rails,
              railIndex,
              dragRailIndex,
              values,
              dragPositions,
              railDimensionsRef,
              railMargins,
              dropLocation,
              service,
              dragItemNode,
              accessory_rails,
              dropArea
            );
          }

          setValues({
            ...values,
            headwalls: rails,
            accessory_rails: accessory_rails,
          });
        } else if (
          dropArea !== "rail" &&
          is_template &&
          showTempWarning &&
          service?.u_id
        ) {
          warningAlert(
            "Removing service may not meet FGI minimum guidelines. Please review before proceeding."
          );
          setShowTempWarning(false);
          return;
        }
        //calculation  for accessories
        else if (service.is_accessory_rail) {
          //drop  services into rails

          if (dropArea === "rail" && model !== PRODUCT.INFINITY) {
            if (service.type === "unimount") {
              // if (rails.length > 1)
              dropLocation = checkUnimountRailDrop(
                newServiceObj,
                rails,
                dragPositions,
                dropLocation,
                accessory_rails,
                model
              );
            } else if (newServiceObj.type === "surface_mounted")
              dropLocation = checkSurfaceMountedRailDrop(
                newServiceObj,
                dragPositions,
                dropLocation,
                accessory_rails,
                railDimensionsRef,
                rails,
                model,
                values,
                railIndex
              );
            else if (newServiceObj.type === "embedded")
              dropLocation = checkEmbeddedRailDrop(
                newServiceObj,
                dragPositions,
                dropLocation,
                accessory_rails,
                rails,
                model,
                values
              );
          } else if (dropArea === "rail" && model === PRODUCT.INFINITY) {
            if (newServiceObj.type === "surface_mounted")
              dropLocation = checkInfinitySurfaceMountedRailDrop(
                newServiceObj,
                dragPositions,
                dropLocation,
                accessory_rails,
                railDimensionsRef,
                rails,
                model,
                values
              );
          }
          if (dropLocation) {
            accessorieRails = handleAccessorieRailDropLocation(
              newServiceObj,
              accessory_rails,
              dragPositions,
              dropArea
            );

            accessory_rails[newServiceObj.type] = accessorieRails;
            setValues({
              ...values,
              accessory_rails: accessory_rails,
            });
          }
        } else if (service.is_accessory) {
          handleAccessoryDropLocation(
            rails,
            accessory_rails,
            dragPositions,
            newServiceObj,
            dropArea,
            railDimensionsRef,
            model,
            values,
            railIndex
          );
        }

        dragItem.current = "";
      }
    },

    [
      headwalls,
      model,
      railMargins,
      setValues,
      values,
      is_template,
      showTempWarning,
      accessory_rails,
    ]
  );

  const handleDrop2 = useCallback(
    (
      e,
      dropArea,
      railIndex = null,
      rowIndex = null,
      item,
      arrow,
      prevY = null
    ) => {
      // e.stopPropagation();

      if (item) {
        setDragEntered(false);
        const rails = [...headwalls];
        const service = e;
        const {
          railIndex: dragRailIndex, //from where service is dragged

          rowIndex: dragRowIndex, //from where service is dragged
        } = item;
        let dropLocation = true;
        // let accessorieRails;
        // let newServiceObj = { ...service };

        //check droplocation only for the services that are dropped on rail and for services
        // if (!service?.is_accessory_rail && !service.is_accessory) {
        if (dropArea === "rail") {
          if (model === PRODUCT.INFINITY)
            dropLocation = checkInfinityDropLocation2(
              railIndex,
              item,
              values,
              dragPositions,
              railDimensionsRef,
              railMargins,
              arrow
            );
          else if (model === PRODUCT.AXIOM) {
            if (service.category === "Gas") railMargins["between"] = 20; //margins for gas service is different
            dropLocation = checkAxiomDropLocation2(
              railIndex,
              item,
              values,
              dragPositions,
              railDimensionsRef,
              railMargins,
              accessory_rails,
              arrow,
              prevY
            );
          } else if (model === PRODUCT.COMPASS) {
            dropLocation = checkCompassDropLocation2(
              rowIndex,
              railIndex,
              item,
              values,
              dragPositions,
              railMargins,
              accessory_rails,
              arrow
            );
          } else if (model === PRODUCT.ARRAY) {
            dropLocation = checkArrayDropLocation2(
              rowIndex,
              railIndex,
              item,
              values,
              dragPositions,
              railMargins,
              railDimensionsRef,
              accessory_rails,
              arrow,
              prevY
            );
          } else if (model === PRODUCT.CONSOLE) {
            dropLocation = checkConsoleDropLocation2(
              railIndex,
              item,
              values,
              dragPositions,
              railMargins,
              railDimensionsRef,
              arrow,
              prevY
            );
          } else if (model === PRODUCT.ELOQUENCE) {
            dropLocation = checkEloquenceDropLocation2(
              railIndex,
              dragItem,
              values,
              dragPositions,
              railMargins,
              railDimensionsRef,
              prevY
            );
          } else if (model === PRODUCT.SECURITY) {
            dropLocation = checkSecurityConsoleDropLocation2(
              railIndex,
              item,
              values,
              dragPositions,
              railMargins,
              railDimensionsRef,
              arrow,
              prevY
            );
          } else if (model === PRODUCT.HIDDEN) {
            dropLocation = checkHiddenHeadwallDropLocation2(
              railIndex,
              dragItem,
              values,
              dragPositions,
              railMargins,
              railDimensionsRef,
              prevY
            );
          }
        } else if (dropArea !== "rail" && is_template && showTempWarning) {
          warningAlert(
            "Please don't remove the services as it may no longer meet the FGI minimum guidelines."
          );
          setShowTempWarning(false);
          return;
        }
        //drop from service panel and interchange of services between panels
        if (model === PRODUCT.INFINITY || model === PRODUCT.AXIOM) {
          handleInfinityAxiomDrop2(
            e,
            rails,
            railIndex,
            dragRailIndex,
            values,
            dragPositions,
            railDimensionsRef,
            railMargins,
            dropLocation,
            service,
            dragItemNode
          );
        }
        //calculation for compass
        else if (model === PRODUCT.COMPASS) {
          handleCompassDrop2(
            e,
            rails,
            railIndex,
            dragRailIndex,
            values,
            dragPositions,
            railDimensionsRef,
            railMargins,
            dropLocation,
            service,
            dragItemNode,
            rowIndex,
            dragRowIndex
          );
        }
        //calculation for array
        else if (model === PRODUCT.ARRAY) {
          handleArrayDrop2(
            e,
            rails,
            railIndex,
            dragRailIndex,
            values,
            dragPositions,
            railDimensionsRef,
            railMargins,
            dropLocation,
            service,
            dragItemNode,
            rowIndex,
            dragRowIndex
          );
        } else if (model === PRODUCT.CONSOLE || model === PRODUCT.SECURITY) {
          handleConsoleDrop2(
            e,
            rails,
            railIndex,
            dragRailIndex,
            values,
            dragPositions,
            railDimensionsRef,
            railMargins,
            dropLocation,
            service,
            dragItemNode,
            rowIndex,
            dragRowIndex
          );
        } else if (model === PRODUCT.ELOQUENCE || model === PRODUCT.HIDDEN) {
          handleEloquenceDrop2(
            e,
            rails,
            railIndex,
            dragRailIndex,
            values,
            dragPositions,
            railDimensionsRef,
            railMargins,
            dropLocation,
            service,
            dragItemNode,
            accessory_rails,
            dropArea
          );
        }
        setValues({ ...values, headwalls: rails });
        dragItem.current = "";
      }
    },
    [
      headwalls,
      model,
      railMargins,
      setValues,
      values,
      is_template,
      showTempWarning,
      accessory_rails,
    ]
  );

  useEffect(() => {
    const handleClick = (event) => {
      if (
        zoomControlsRef.current &&
        zoomControlsRef.current.contains(event.target)
      ) {
        return; // Ignore click on zoom buttons
      }

      // Reset zoom no matter where user clicks (except zoom buttons)
      setScale(1);
    };

    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => {
    const handlDragServOutside = (e) => {
      e.preventDefault();
    };

    const handlDropServOutside = (e) => {
      handleDrop(e, "wall");
    };

    // Add event listener when the component mounts
    document.addEventListener("dragover", handlDragServOutside);
    document.addEventListener("drop", handlDropServOutside);

    // Clean up the event listener when the component unmounts
    return () => {
      document.removeEventListener("drop", handlDropServOutside);
    };
  }, [handleDrop]);

  const handleBedGraphic = () => {
    setShowBedGraphic(!showBedGraphic);
    outeWwallRef.current.scrollTo({
      top: outeWwallRef.current.scrollHeight,
      behavior: "smooth",
    });
  };

  const zoomIn = () => setScale((prev) => Math.min(prev + 0.2, 3));
  const zoomOut = () => setScale((prev) => Math.max(prev - 0.2, 1));

  const newRenderContent = () => (
    <div className="relative w-full h-full">
      <div
        ref={outeWwallRef}
        className="wall overflow-auto p-4 relative w-full"
        onDragOver={(e) => handleonDragOver(e, "wall")}
        onDrop={(e) => handleDrop(e, "wall")}
        style={{ pointerEvents: "auto" }}
      >
        {(!designId &&
          model &&
          (model === PRODUCT.INFINITY ||
            model === PRODUCT.ARRAY ||
            model === PRODUCT.COMPASS) &&
          step === 1) ||
        (!designId &&
          (model !== PRODUCT.INFINITY ||
            model !== PRODUCT.ARRAY ||
            model !== PRODUCT.COMPASS) &&
          sub_model &&
          step === 2) ? (
          <Templates setShowTempWarning={setShowTempWarning} />
        ) : model !== PRODUCT.INFINITY &&
          model !== PRODUCT.ARRAY &&
          model !== PRODUCT.COMPASS &&
          !sub_model ? null : (
          <HeadWalls
            values={values}
            ruler={ruler}
            showBedGraphic={showBedGraphic}
            handleonDragOver={handleonDragOver}
            handleDrop={handleDrop}
            handleDragStart={handleDragStart}
            handleDragEnter={handleDragEnter}
            clickArrowLeft={clickArrowLeft}
            clickArrowRight={clickArrowRight}
            clickArrowDown={clickArrowDown}
            clickArrowUp={clickArrowUp}
            dragEntered={dragEntered}
            wallLength={wallWidth}
            ref={wallRef}
            setDragEntered={setDragEntered}
            service={dragItem?.current}
          />
        )}
      </div>
    </div>
  );

  const renderContent = () => (
    <div className="relative w-full h-full">
      {/* Zoom Buttons */}
      {step !== 1 && (
        <div
          ref={zoomControlsRef}
          className="absolute bottom-4 right-4 z-50 bg-white rounded-[15px] shadow-xl px-4 py-3 flex flex-col items-center justify-between space-y-2 w-12 h-22"
        >
          <button
            onClick={zoomIn}
            className="text-teal-800 text-2xl font-bold leading-none"
          >
            +
          </button>
          <hr className="w-10 border-teal-800" />
          <button
            onClick={zoomOut}
            className="text-teal-800 text-2xl font-bold leading-none"
          >
            –
          </button>
        </div>
      )}

      {/* Wall Area */}
      <div
        ref={outeWwallRef}
        className="wall overflow-auto p-4 relative w-full h-full"
        onDragOver={(e) => handleonDragOver(e, "wall")}
        onDrop={(e) => handleDrop(e, "wall")}
        style={{
          pointerEvents: "auto",
        }}
      >
        <div
          style={{
            transform: step === 1 ? "scale(1)" : `scale(${scale})`,
            transformOrigin: "top left",
            transition: "transform 0.3s ease",
            width: "100%",
          }}
        >
          {(!designId &&
            model &&
            (model === PRODUCT.INFINITY ||
              model === PRODUCT.ARRAY ||
              model === PRODUCT.COMPASS) &&
            step === 1) ||
          (!designId &&
            (model !== PRODUCT.INFINITY ||
              model !== PRODUCT.ARRAY ||
              model !== PRODUCT.COMPASS) &&
            sub_model &&
            step === 2) ? (
            <Templates setShowTempWarning={setShowTempWarning} />
          ) : model !== PRODUCT.INFINITY &&
            model !== PRODUCT.ARRAY &&
            model !== PRODUCT.COMPASS &&
            !sub_model ? null : (
            <HeadWalls
              values={values}
              ruler={ruler}
              showBedGraphic={showBedGraphic}
              handleonDragOver={handleonDragOver}
              handleDrop={handleDrop}
              handleDragStart={handleDragStart}
              handleDragEnter={handleDragEnter}
              clickArrowLeft={clickArrowLeft}
              clickArrowRight={clickArrowRight}
              clickArrowDown={clickArrowDown}
              clickArrowUp={clickArrowUp}
              dragEntered={dragEntered}
              wallLength={wallWidth}
              ref={wallRef}
              setDragEntered={setDragEntered}
              service={dragItem?.current}
            />
          )}
        </div>
      </div>
    </div>
  );
  // console.log({"tet":values.headwalls})
  return (
    <>
      <div className="wall_contatiner">
        <div className="mt-9">
          <Stepper currentStep={step} />
        </div>
        <div className="mt-6 mb-5 text-base font-bold flex justify-between">
          <div className="flex items-center txtBlue titleh2Txt">
            {models[model]}
          </div>
          {step >= 3 && (
            <div className="flex items-center rootPathTxt">
              <Editable values={values} setValues={setValues} />

              <Tooltip
                class_name={"cursor-pointer ml-5"}
                icon={DiscardIcon}
                width={36}
                height={36}
                handleClick={handleDelete}
                title={"Discard Design"}
              />
            </div>
          )}
        </div>
        {/* ruler and bed icon */}
        {step <= 5 ? (
          <div className="wall relative">
            {model && step > 2 && (
              <>
                <Tooltip
                  class_name={`scaleIcon ${
                    ruler ? "opacity-100" : "opacity-50"
                  }`}
                  icon={ScaleIcon}
                  handleClick={() => setRuler(!ruler)}
                  title={"Ruler"}
                  position={"right"}
                />
                <Tooltip
                  class_name={`bedIcon ${
                    showBedGraphic ? "opacity-100" : "opacity-50"
                  }`}
                  icon={BedIcon}
                  handleClick={handleBedGraphic}
                  title={"Bed Graphic"}
                />
              </>
            )}
            {step <= 2 ? newRenderContent() : renderContent()}
          </div>
        ) : (
          <div
            className="flex justify-center  relative headwall_bg"
            style={{ height: `calc(100vh - 223px)` }}
          >
            <TransformWrapper defaultScale={1}>
              {({ zoomIn, zoomOut }) => (
                <>
                  <div className="btn_container">
                    <button className="btn" onClick={() => zoomIn()}>
                      +
                    </button>
                    <button className="btn" onClick={() => zoomOut()}>
                      -
                    </button>
                  </div>
                  <TransformComponent
                    contentStyle={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: `calc(100vh - 240px)`,
                      // width:"auto",
                      minWidth: wallWidth ? wallWidth : "auto",
                    }}
                  >
                    <img
                      src={design_image}
                      alt="..img"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                      }}
                    />
                  </TransformComponent>
                </>
              )}
            </TransformWrapper>
          </div>
        )}
      </div>

      <RightPanel
        children={component}
        handleGenerateImage={generateImage}
        designId={designId}
        designName={designName}
      />
    </>
  );
}

export default Editor;
